import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { DOCUMENT } from '@angular/common';

export interface GTMConfig {
  id: string;
  environment?: string;
  debug?: boolean;
}

export interface GTMEvent {
  event: string;
  [key: string]: unknown;
}

@Injectable({
  providedIn: 'root',
})
export class GoogleTagManagerService {
  private gtmId: string | null = null;
  private isInitialized = false;
  private isBrowser: boolean;

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  /**
   * Initializes Google Tag Manager
   * @param config GTM configuration
   */
  initialize(config: GTMConfig): void {
    if (!this.isBrowser || this.isInitialized) {
      return;
    }

    this.gtmId = config.id;
    this.isInitialized = true;

    this.loadGTMScript();
    this.initializeDataLayer();
    this.pushGtmStartEvent();
  }

  /**
   * Checks if GTM is initialized
   * @returns boolean indicating if GTM is initialized
   */
  isGtmInitialized(): boolean {
    return this.isInitialized;
  }

  /**
   * Resets GTM state (useful for testing or re-initialization scenarios)
   */
  reset(): void {
    this.isInitialized = false;
    this.gtmId = null;
  }

  /**
   * Sends an event to GTM
   * @param event Event object
   */
  pushEvent(event: GTMEvent): void {
    if (!this.isBrowser || !this.isInitialized) {
      console.warn('GTM não inicializado ou não está no browser');
      return;
    }

    if (window.dataLayer) {
      window.dataLayer.push(event);
    }
  }

  /**
   * Sends a page view event
   * @param pageName Page name
   * @param pagePath Page path
   */
  trackPageView(pageName?: string, pagePath?: string): void {
    const event: GTMEvent = {
      event: 'page_view',
      page_title: pageName || document.title,
      page_location: pagePath || window.location.href,
      page_path: pagePath || window.location.pathname,
    };

    this.pushEvent(event);
  }

  /**
   * Sends a click event
   * @param element Clicked element
   * @param action Action performed
   * @param category Element category
   */
  trackClick(element: string, action: string, category?: string): void {
    const event: GTMEvent = {
      event: 'click',
      element: element,
      action: action,
      category: category || 'interaction',
    };

    this.pushEvent(event);
  }

  /**
   * Sends a form event
   * @param formName Form name
   * @param action Form action (submit, start, complete)
   * @param step Form step (optional)
   */
  trackForm(formName: string, action: 'start' | 'submit' | 'complete', step?: string): void {
    const event: GTMEvent = {
      event: 'form_interaction',
      form_name: formName,
      form_action: action,
      form_step: step,
    };

    this.pushEvent(event);
  }

  /**
   * Sends a navigation event
   * @param from From page
   * @param to To page
   * @param method Navigation method
   */
  trackNavigation(from: string, to: string, method: 'click' | 'programmatic' | 'back' | 'forward' = 'click'): void {
    const event: GTMEvent = {
      event: 'navigation',
      from_page: from,
      to_page: to,
      navigation_method: method,
    };

    this.pushEvent(event);
  }

  /**
   * Sends an error event
   * @param error Error message
   * @param errorType Error type
   * @param component Component where the error occurred
   */
  trackError(error: string, errorType: string, component?: string): void {
    const event: GTMEvent = {
      event: 'error',
      error_message: error,
      error_type: errorType,
      error_component: component,
    };

    this.pushEvent(event);
  }

  /**
   * Sends a custom business event
   * @param eventName Event name
   * @param parameters Additional parameters
   */
  trackCustomEvent(eventName: string, parameters: Record<string, unknown> = {}): void {
    const event: GTMEvent = {
      event: eventName,
      ...parameters,
    };

    this.pushEvent(event);
  }

  /**
   * Checks whether GTM is initialized
   */
  isGTMInitialized(): boolean {
    return this.isInitialized && this.isBrowser;
  }

  /**
   * Gets the configured GTM ID
   */
  getGTMId(): string | null {
    return this.gtmId;
  }

  private loadGTMScript(): void {
    if (this.document.getElementById('gtm-script')) {
      return;
    }

    const script = this.document.createElement('script');
    script.id = 'gtm-script';
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${this.gtmId}`;
    this.document.head.appendChild(script);
  }

  private initializeDataLayer(): void {
    if (!window.dataLayer) {
      window.dataLayer = [];
    }
  }

  // AB#75063 The Datadog RUM tag of every container fires on the gtm.js start event; keep this push.
  private pushGtmStartEvent(): void {
    if (!this.gtmId) {
      return;
    }

    window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
  }
}

// Global declaration for dataLayer
declare global {
  interface Window {
    dataLayer: unknown[];
  }
}
